import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

const LG_MESSAGES_HINT = `{
  "messages": {
    "reducer": "append",
    "default": []
  }
}`;

/**
 * Edits `definition.state_schema` → Python `flow.state_schema` (LangGraph state hints).
 */
export default function StudioV2FlowStateSchemaEditor({ text, setText }) {
  const [tab, setTab] = useState(0);
  const [local, setLocal] = useState('{}');
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const d = JSON.parse(text || '{}');
      const s = d.state_schema;
      setLocal(JSON.stringify(s != null && typeof s === 'object' ? s : {}, null, 2));
      setError(null);
    } catch (e) {
      setError(e.message || 'Invalid JSON');
    }
  }, [text]);

  const commit = (jsonStr) => {
    let parsed;
    try {
      parsed = JSON.parse((jsonStr ?? local).trim() || '{}');
    } catch (e) {
      setError(e.message);
      return;
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setError('State schema must be a JSON object.');
      return;
    }
    try {
      const d = JSON.parse(text || '{}');
      const next = { ...d, state_schema: parsed };
      setText(JSON.stringify(next, null, 2));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>State schema</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Optional LangGraph-style schema (
            <code>state_schema</code>
            ). Describes reducers and defaults for channels the executor merges across steps.
          </Typography>
        </Box>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36 }}>
          <Tab label="JSON" sx={{ minHeight: 36, py: 0.5 }} />
          <Tab label="Notes" sx={{ minHeight: 36, py: 0.5 }} />
        </Tabs>
        {error && <Alert severity="error">{error}</Alert>}
        {tab === 0 && (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  setLocal('{}');
                  commit('{}');
                }}
              >
                Clear to {'{}'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  setLocal(LG_MESSAGES_HINT);
                  commit(LG_MESSAGES_HINT);
                }}
              >
                Insert messages + append reducer (template)
              </Button>
            </Stack>
            <TextField
              multiline
              minRows={12}
              fullWidth
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              onBlur={() => commit()}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
            />
          </>
        )}
        {tab === 1 && (
          <Alert severity="info" icon={false}>
            <Typography variant="body2" component="div" sx={{ mb: 1 }}>
              Legacy Studio treated per-node state vars separately; in V2 the graph still passes
              {' '}
              <code>state_schema</code>
              {' '}
              through to the same Python executor. Align channel names with keys your code / LLM nodes read and write.
            </Typography>
            <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              {LG_MESSAGES_HINT}
            </Typography>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
